import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline"
              className="border-2 border-red-400"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Terms and Conditions</h1>
          <p className="text-gray-700 text-lg font-medium">Last updated: November 2, 2025</p>
        </div>

        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using the Netix Fibernet Referral Program ("the Program"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Referral Program Overview</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                The Netix Fibernet Referral Program allows registered users to earn commissions by referring new customers to our services. Commissions are earned when:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>A referred customer places an order through your unique referral link</li>
                <li>The order is successfully completed and payment is received</li>
                <li>The customer maintains active service for the minimum qualifying period</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Commission Structure</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Commission rates are tiered based on your monthly sales performance:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Starter Tier (0-5 sales/month):</strong> 5% commission</li>
                <li><strong>Pro Tier (6-15 sales/month):</strong> 8% commission</li>
                <li><strong>Elite Tier (16+ sales/month):</strong> 12% commission</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                Commission rates may be adjusted at Netix Fibernet's discretion with prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Payment Terms</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Commissions are paid monthly on achieving minimum threshold of ₹1,000</li>
                <li>Payments are processed within 30 days of the end of each month</li>
                <li>Valid bank account or payment method details must be provided</li>
                <li>Commissions remain pending until customer payment is confirmed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. User Obligations</h2>
              <p className="text-gray-700 leading-relaxed mb-3">As a program participant, you agree to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate and truthful information about Netix Fibernet services</li>
                <li>Not engage in spam, fraudulent activities, or misleading advertising</li>
                <li>Not create fake accounts or self-referrals</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Maintain confidentiality of your account credentials</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Prohibited Activities</h2>
              <p className="text-gray-700 leading-relaxed mb-3">The following activities are strictly prohibited:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Creating fake or fraudulent referrals</li>
                <li>Using automated systems or bots to generate referrals</li>
                <li>Misrepresenting Netix Fibernet services or pricing</li>
                <li>Using spam or unsolicited messages to promote referral links</li>
                <li>Violating any third-party platform's terms of service</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                Violation of these terms may result in immediate account suspension and forfeiture of pending commissions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Account Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                Netix Fibernet reserves the right to suspend or terminate any account that violates these terms, engages in fraudulent activities, or is deemed detrimental to the program. In case of termination, all pending commissions may be forfeited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content, trademarks, and materials provided through the program remain the property of Netix Fibernet. You may use provided marketing materials solely for the purpose of promoting Netix Fibernet services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                Netix Fibernet shall not be liable for any indirect, incidental, special, or consequential damages arising out of or related to your use of the referral program. Our liability is limited to the amount of unpaid commissions owed to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                Netix Fibernet reserves the right to modify these terms at any time. Continued use of the program after changes constitutes acceptance of the modified terms. We will notify users of significant changes via email or dashboard notification.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these terms, please contact us at:
              </p>
              <div className="mt-3 text-gray-700 space-y-1">
                <p><strong>Email:</strong> support@netixfibernet.com</p>
                <p data-testid="terms-contact-mobile"><strong>Phone:</strong> +91 93199 19988</p>
                <p data-testid="terms-office-phone"><strong>Office Phone:</strong> 06276 - 459015</p>
                <p><strong>Office Locations:</strong> Delhi, Haryana, Bihar, Uttar Pradesh</p>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-300">
              <p className="text-sm text-gray-600 italic">
                By participating in the Netix Fibernet Referral Program, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsAndConditions;
